import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../assets/styles/AboutPage.css';
import '../assets/styles/EnhancedAboutFooter.css';
import '../assets/styles/EnhancedLeadership.css';
import { Link } from 'react-router-dom';

const AboutPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>About Translate Stocks | AI-Powered Trading Platform</title>
        <meta name="description" content="Learn about Translate Stocks, our mission to revolutionize trading with AI-powered tools, and the team behind Price on Rails technology." />
        <meta name="keywords" content="about translate stocks, AI trading company, Price on Rails developer, trading technology team" />
      </Helmet>
      
      <Header />
      
      <main className="about-page">
        <section className="hero-section about-hero">
          <div className="container">
            <div className="hero-content">
              <h1>Our Mission Is to Redefine <span className="highlight">What's Possible</span> in Trading</h1>
              <p className="hero-subtitle">
                Translate Stocks was founded on a simple belief: trading technology should be as intelligent 
                as the people who use it. Today, we're making that vision a reality.
              </p>
            </div>
          </div>
        </section>
        
        <section className="about-story">
          <div className="container">
            <div className="about-grid">
              <div className="about-content">
                <h2>Our Story</h2>
                <p>
                  Translate Stocks began in 2018 when a veteran trader and AI engineer came  
                  with a frustration: existing trading platforms weren't keeping pace with advancements 
                  in artificial intelligence and machine learning.
                </p>
                <p>
                  Our founders spent years developing algorithmic trading systems and saw firsthand how 
                  predictive technology could transform decision-making in fast-moving markets. But these 
                  tools were typically available only to institutional investors with deep resources.
                </p>
                <p>
                  We set out to democratize access to AI-powered trading tools, building a platform that 
                  leverages cutting-edge technology while maintaining an intuitive, accessible interface 
                  for traders of all experience levels.
                </p>
                <p>
                  The result is Price on Rails, our groundbreaking predictive chart technology, and the 
                  Agentic Bot that brings automated execution to traders across global markets.
                </p>
              </div>
              <div className="about-image">
                <img 
                  src="/images/about-team.jpg" 
                  alt="Translate Stocks founding team" 
                  width="540" 
                  height="400"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
        
        <section className="about-values">
          <div className="container">
            <h2 className="section-title">Our Core Values</h2>
            
            <div className="values-grid">
              <div className="value-card">
                <div className="value-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 4C12.95 4 4 12.95 4 24C4 35.05 12.95 44 24 44C35.05 44 44 35.05 44 24C44 12.95 35.05 4 24 4ZM32 26H26V32C26 33.1 25.1 34 24 34C22.9 34 22 33.1 22 32V26H16C14.9 26 14 25.1 14 24C14 22.9 14.9 22 16 22H22V16C22 14.9 22.9 14 24 14C25.1 14 26 14.9 26 16V22H32C33.1 22 34 22.9 34 24C34 25.1 33.1 26 32 26Z" fill="#2563EB"/>
                  </svg>
                </div>
                <h3>Innovation First</h3>
                <p>We're constantly pushing the boundaries of what's possible in trading technology, with a relentless commitment to improvement.</p>
              </div>
              
              <div className="value-card">
                <div className="value-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 4C12.95 4 4 12.95 4 24C4 35.05 12.95 44 24 44C35.05 44 44 35.05 44 24C44 12.95 35.05 4 24 4ZM24 40C15.18 40 8 32.82 8 24C8 15.18 15.18 8 24 8C32.82 8 40 15.18 40 24C40 32.82 32.82 40 24 40ZM31 28.18L29.4 26.18L21 32.94V16H18V36L31 28.18Z" fill="#2563EB"/>
                  </svg>
                </div>
                <h3>Trader-Centered Design</h3>
                <p>Every feature we build starts with the trader's experience. If it doesn't make decision-making faster or more informed, we don't build it.</p>
              </div>
              
              <div className="value-card">
                <div className="value-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M38 8H10C7.8 8 6 9.8 6 12V36C6 38.2 7.8 40 10 40H38C40.2 40 42 38.2 42 36V12C42 9.8 40.2 8 38 8ZM38 36H10V24H38V36ZM38 20H10V12H38V20Z" fill="#2563EB"/>
                  </svg>
                </div>
                <h3>Transparency</h3>
                <p>We believe in full transparency about how our technology works. While our algorithms are proprietary, traders always understand the basis of our predictions.</p>
              </div>
              
              <div className="value-card">
                <div className="value-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 24C25.32 24 28 21.32 28 18C28 14.68 25.32 12 22 12C18.68 12 16 14.68 16 18C16 21.32 18.68 24 22 24ZM22 16C23.08 16 24 16.92 24 18C24 19.08 23.08 20 22 20C20.92 20 20 19.08 20 18C20 16.92 20.92 16 22 16ZM38 26C40.2 26 42 24.2 42 22C42 19.8 40.2 18 38 18C35.8 18 26 22 26 22C26 22 35.8 26 38 26ZM10 26C12.2 26 22 22 22 22C22 22 12.2 18 10 18C7.8 18 6 19.8 6 22C6 24.2 7.8 26 10 26ZM22 28C16.7 28 6 30.66 6 36V40H38V36C38 30.66 27.3 28 22 28ZM34 36H10V36.02C10.4 34.56 16.16 32 22 32C27.86 32 33.64 34.58 34 36.02V36Z" fill="#2563EB"/>
                  </svg>
                </div>
                <h3>Community</h3>
                <p>We're building more than just a platform—we're nurturing a community of forward-thinking traders who share insights and strategies.</p>
              </div>
            </div>
          </div>
        </section>
        
        <section className="about-leadership">
          <div className="container">
            <h2 className="section-title">Leadership Team</h2>
            
            <div className="team-grid">
              <div className="team-member">
                <div className="member-image">
                  <img 
                    src="/images/cto-portrait.jpg" 
                    alt="Carlos Martins, CTO & CEO" 
                    width="300" 
                    height="300"
                    loading="lazy"
                  />
                </div>
                <h3>Carlos Martins</h3>
                <p className="member-title">CTO & Interim CEO</p>
                <p className="member-bio">
                  AI researcher with extensive experience in financial technologies. MSc in Nuclear Engineering and postgrad in AI from Harvard. Led the development of Price on Rails and oversees strategic direction and technological innovation.
                </p>
              </div>
              
              <div className="team-member">
                <div className="member-image">
                  <img 
                    src="/images/cbdo-portrait.jpg" 
                    alt="Munir Antum, Chief Business Development Officer" 
                    width="300" 
                    height="300"
                    loading="lazy"
                  />
                </div>
                <h3>Munir Antum</h3>
                <p className="member-title">Chief Business Development Officer</p>
                <p className="member-bio">
                  Strategic growth leader with 30+ years in financial markets. Specializes in identifying opportunities, forging partnerships, and driving revenue growth through market expansion and long-term business alliances.
                </p>
              </div>
              
              <div className="team-member">
                <div className="member-image">
                  <img 
                    src="/images/chro-portrait.jpg" 
                    alt="Juliana Rezende, CHRO" 
                    width="300" 
                    height="300"
                    loading="lazy"
                  />
                </div>
                <h3>Juliana Rezende</h3>
                <p className="member-title">Chief Human Resources Officer</p>
                <p className="member-bio">
                  Experienced HR executive with 20+ years in talent management. Previously at Trader Grafico, now drives HR strategy focused on leadership development, employee engagement, and aligning talent with business objectives.
                </p>
              </div>
            </div>
          </div>
        </section>
        
        <section className="about-stats">
          <div className="container">
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-number">2018</span>
                <span className="stat-label">U.S. Founded</span>
              </div>
              
              <div className="stat-item">
                <span className="stat-number">20+</span>
                <span className="stat-label">Years of Experience</span>
              </div>
              
              <div className="stat-item">
                <span className="stat-number">12M+</span>
                <span className="stat-label">Trades Analyzed</span>
              </div>
              
              <div className="stat-item">
                <span className="stat-number">10k+</span>
                <span className="stat-label">Active Traders</span>
              </div>
            </div>
          </div>
        </section>
        
        <section className="about-cta">
          <div className="container">
            <div className="cta-wrapper">
              <h2>Ready to Experience the <span className="highlight">Difference?</span></h2>
              <p>Join thousands of traders who have transformed their approach with our innovative tools.</p>
              <div className="cta-buttons">
                <a href="/pricing" className="btn btn-primary btn-large">Sign&nbsp;Up</a>
                <Link to="/contact" className="btn btn-outline btn-large">Contact Us</Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      
    </>
  );
};

export default AboutPage;